import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import {
  buildWhatsAppMessage, effectivePrice, calcVolume, formatEur, ceil10, copyText, uid, parseDurationDiscounts, waMarkupToHtml,
} from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import {
  X, ArrowLeft, ArrowRight, Check, Plus, Minus, Trash2, Search, Package, Maximize2,
  Camera, FileText, Truck, User, MessageCircle, Copy, Loader2, Sparkles, Upload,
  RotateCcw, Gift, ListChecks, Wand2, Save, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const MODE_META = {
  objects: { title: "Per oggetti", icon: Package },
  area: { title: "Per area", icon: Maximize2 },
  image: { title: "Da immagine", icon: Camera },
  text: { title: "Da testo", icon: FileText },
};

// step keys per base mode
const STEPS = {
  objects: ["items", "ritiro", "cliente", "riepilogo", "result"],
  area: ["area", "ritiro", "cliente", "riepilogo", "result"],
  text: ["text_input", "ritiro", "cliente", "riepilogo", "result"],
  image: ["image_upload", "ritiro", "cliente", "riepilogo", "result"],
};

const STEP_TITLES = {
  items: "Aggiungi gli oggetti",
  area: "Dimensioni dello spazio",
  text_input: "Testo e oggetti",
  image_upload: "Foto e oggetti",
  ritiro: "Ritiro a domicilio",
  cliente: "Dati del cliente",
  riepilogo: "Riepilogo",
  result: "Messaggio pronto",
};

const StepShell = ({ icon: Icon, title, subtitle, children }) => (
  <div className="space-y-5">
    <div className="flex items-start gap-3">
      <div className="h-11 w-11 rounded-xl bg-[#1a5f5a]/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#1a5f5a]" />
      </div>
      <div>
        <h2 className="font-display font-semibold text-2xl text-slate-900 leading-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

/* ---------------- Objects step ---------------- */
function ItemsStep() {
  const { catalog, items, addCatalogItem, setItemQty, addCustomItem, removeItem, updateItem, discounts, monthlyTotal } = useQuote();
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const matches = q ? catalog.filter((c) => (c.label || "").toLowerCase().includes(q)).slice(0, 12) : [];
  const qtyOf = (label) => items.filter((i) => i.tipo === label).reduce((s, i) => s + i.qty, 0);

  return (
    <StepShell icon={Package} title={STEP_TITLES.items} subtitle="Cerca gli oggetti nel catalogo e aggiungili. Puoi anche inserire un oggetto personalizzato.">
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca un oggetto… (es. divano)" className="pl-9" data-testid="wizard-catalog-search" autoFocus />
      </div>

      {q && (
        <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1" data-testid="wizard-catalog-results">
          {matches.length === 0 && <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-md">Nessun oggetto trovato.</p>}
          {matches.map((it) => {
            const cnt = qtyOf(it.label);
            const inQuote = items.find((i) => i.tipo === it.label);
            return (
              <div key={it.label} className="flex items-center justify-between gap-2 border border-slate-200 rounded-md p-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">{it.label}</div>
                  <div className="text-xs text-slate-400">€{formatEur(it.price_eur)}/mese · {it.length_cm}×{it.width_cm}×{it.height_cm}cm</div>
                </div>
                {cnt > 0 ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setItemQty(inQuote.id, inQuote.qty - 1)}><Minus className="h-3.5 w-3.5" /></Button>
                    <span className="w-5 text-center text-sm font-semibold">{cnt}</span>
                    <Button size="icon" className="h-7 w-7 bg-[#1a5f5a] hover:bg-[#134744]" onClick={() => addCatalogItem(it)} data-testid={`wizard-cat-add-${it.label}`}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : (
                  <Button size="icon" className="h-8 w-8 bg-[#1a5f5a] hover:bg-[#134744] shrink-0" onClick={() => addCatalogItem(it)} data-testid={`wizard-cat-add-${it.label}`}><Plus className="h-4 w-4" /></Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="border border-slate-200 rounded-lg p-4" data-testid="wizard-items-list">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-slate-700">Oggetti nel preventivo ({items.length})</span>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={addCustomItem} data-testid="wizard-add-custom"><Plus className="h-4 w-4" /> Personalizzato</Button>
        </div>
        {items.length === 0 ? (
          <p className="text-slate-400 text-sm py-3 text-center border border-dashed border-slate-200 rounded-md">Nessun oggetto aggiunto ancora.</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const price = effectivePrice(it, discounts);
              return (
                <div key={it.id} className="flex items-center gap-2 border border-slate-100 bg-slate-50 rounded-md p-2.5" data-testid={`wizard-item-${it.id}`}>
                  <Input value={it.tipo} onChange={(e) => updateItem(it.id, { tipo: e.target.value })} className="h-8 flex-1 min-w-0 bg-white text-sm" />
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setItemQty(it.id, it.qty - 1)}><Minus className="h-3.5 w-3.5" /></Button>
                    <span className="w-5 text-center text-sm font-semibold">{it.qty}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setItemQty(it.id, it.qty + 1)}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                  <span className="w-16 text-right text-sm font-semibold text-[#1a5f5a] shrink-0">€{formatEur(price * it.qty)}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 shrink-0" onClick={() => removeItem(it.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              );
            })}
          </div>
        )}
        {items.length > 0 && (
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs uppercase tracking-wide text-slate-400">Totale mensile</span>
            <span className="text-lg font-display font-semibold text-[#1a5f5a]" data-testid="wizard-items-total">€{formatEur(monthlyTotal)}/mese</span>
          </div>
        )}
      </div>
    </StepShell>
  );
}

/* ---------------- Area step ---------------- */
function AreaStep() {
  const { areaData, setAreaData, settings } = useQuote();
  const upd = (patch) => setAreaData((a) => ({ ...a, ...patch }));
  const volNetto = (areaData.area || 0) * (areaData.altezza || 0) * ((areaData.riempimento || 0) / 100);
  const prezzo = volNetto * (areaData.tariffaM3 || 0);
  return (
    <StepShell icon={Maximize2} title={STEP_TITLES.area} subtitle="Indica quanto spazio serve: calcoliamo automaticamente volume e prezzo.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Area (m²)</Label>
          <Input type="number" value={areaData.area || ""} onChange={(e) => upd({ area: parseFloat(e.target.value) || 0 })} placeholder="0" className="mt-1" data-testid="wizard-area-input" autoFocus />
        </div>
        <div>
          <Label className="text-xs">Altezza soffitto (m)</Label>
          <Input type="number" step="0.1" value={areaData.altezza} onChange={(e) => upd({ altezza: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="wizard-area-altezza" />
        </div>
      </div>
      <div>
        <div className="flex justify-between mb-2"><Label className="text-xs">Riempimento</Label><span className="text-sm font-semibold text-[#1a5f5a]">{areaData.riempimento}%</span></div>
        <Slider value={[areaData.riempimento]} min={10} max={100} step={5} onValueChange={([v]) => upd({ riempimento: v })} data-testid="wizard-area-riempimento" />
      </div>
      <div>
        <Label className="text-xs">Tariffa (€/m³)</Label>
        <Input type="number" value={areaData.tariffaM3} onChange={(e) => upd({ tariffaM3: parseFloat(e.target.value) || 0, isTariffaOverridden: true })} className="mt-1" data-testid="wizard-area-tariffa" />
      </div>
      <div className="bg-[#1a5f5a] text-white rounded-lg p-5 grid grid-cols-2 gap-4">
        <div><div className="text-xs uppercase tracking-[0.2em] text-white/60">Volume netto</div><div className="text-2xl font-display font-semibold mt-1" data-testid="wizard-area-volume">{volNetto.toFixed(2)} m³</div></div>
        <div className="text-right"><div className="text-xs uppercase tracking-[0.2em] text-white/60">Prezzo mensile</div><div className="text-2xl font-display font-semibold mt-1" data-testid="wizard-area-prezzo">€{formatEur(prezzo)}</div></div>
      </div>
    </StepShell>
  );
}

/* ---------------- Catalog suggestion (add more from catalog) ---------------- */
function CatalogSuggestion({ extras, setExtras }) {
  const { catalog } = useQuote();
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const matches = q ? catalog.filter((c) => (c.label || "").toLowerCase().includes(q)).slice(0, 8) : [];
  const add = (c) => {
    setExtras((prev) => {
      const ex = prev.find((e) => e.label === c.label);
      if (ex) return prev.map((e) => (e.label === c.label ? { ...e, qty: e.qty + 1 } : e));
      return [...prev, { id: uid(), label: c.label, qty: 1, price: c.price_eur }];
    });
    setSearch("");
  };
  const setQty = (label, delta) => setExtras((prev) => prev.flatMap((e) => {
    if (e.label !== label) return [e];
    const n = e.qty + delta; return n <= 0 ? [] : [{ ...e, qty: n }];
  }));

  return (
    <div className="rounded-lg border border-dashed border-[#1a5f5a]/40 bg-[#1a5f5a]/5 p-3 space-y-2" data-testid="wizard-catalog-suggestion">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <Package className="h-4 w-4 text-[#1a5f5a]" /> Aggiungi altro dal catalogo <span className="text-xs font-normal text-slate-400">(facoltativo)</span>
      </div>
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca un oggetto dal catalogo…" className="pl-9 bg-white" data-testid="wizard-extra-search" />
      </div>
      {q && (
        <div className="space-y-1.5 max-h-[180px] overflow-y-auto" data-testid="wizard-extra-results">
          {matches.length === 0 && <p className="text-xs text-slate-400 py-1">Nessun oggetto trovato.</p>}
          {matches.map((it) => (
            <div key={it.label} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
              <span className="text-sm text-slate-700 truncate">{it.label} <span className="text-xs text-slate-400">€{formatEur(it.price_eur)}</span></span>
              <Button size="icon" className="h-7 w-7 bg-[#1a5f5a] hover:bg-[#134744] shrink-0" onClick={() => add(it)} data-testid={`wizard-extra-add-${it.label}`}><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="wizard-extra-list">
          {extras.map((e) => (
            <span key={e.id} className="inline-flex items-center gap-1 bg-white border border-[#1a5f5a]/30 rounded-full pl-2.5 pr-1 py-0.5 text-sm text-slate-700">
              {e.label}
              <button className="ml-1 text-slate-400 hover:text-red-500" onClick={() => setQty(e.label, -1)}><Minus className="h-3 w-3" /></button>
              <span className="w-4 text-center text-xs font-semibold">{e.qty}</span>
              <button className="text-slate-400 hover:text-[#1a5f5a]" onClick={() => setQty(e.label, 1)}><Plus className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Text step (input + inline review) ---------------- */
function TextStep({ textResults, setTextResults, extras, setExtras }) {
  const { inputText, setInputText, catalog, setRitiro, setClienteNome, setAbitiBox } = useQuote();
  const [loading, setLoading] = useState(false);
  const findCat = (label) => catalog.find((c) => c.label.toLowerCase() === String(label || "").toLowerCase());
  const complete = (r) => r.l && r.w && r.h;
  const setField = (id, patch) => setTextResults((rs) => rs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const CONF = { high: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700" };

  const analyze = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/extract-items", { text: inputText });
      let list = data.items.map((it) => {
        const c = findCat(it.catalogMatch) || findCat(it.label);
        return { ...it, id: uid(), qty: it.qty || 1, include: true,
          l: it.length_cm || (c ? c.length_cm : "") || "", w: it.width_cm || (c ? c.width_cm : "") || "", h: it.height_cm || (c ? c.height_cm : "") || "" };
      });
      const missing = list.filter((r) => !complete(r));
      if (missing.length) {
        try {
          const { data: est } = await api.post("/ai/estimate-prices", { items: missing.map((m) => ({ label: m.label, dims: { l: +m.l || 0, w: +m.w || 0, h: +m.h || 0 } })) });
          const byLabel = {}; (est.estimates || []).forEach((e) => { byLabel[String(e.label).toLowerCase()] = e; });
          list = list.map((r) => { if (complete(r)) return r; const e = byLabel[String(r.label).toLowerCase()]; if (!e) return r; return { ...r, l: r.l || e.length_cm || "", w: r.w || e.width_cm || "", h: r.h || e.height_cm || "" }; });
        } catch { /* ignore */ }
      }
      setTextResults(list);
      if (data.cap) setRitiro((r) => ({ ...r, cap: data.cap }));
      const nm = (data.clientName || "").trim();
      const nmCap = nm ? nm.charAt(0).toUpperCase() + nm.slice(1) : "";
      if (nmCap) setClienteNome(nmCap);
      if (/\b(abiti|abito|vestit|capi|capo|camic|magliett|cappott|piumin|giacc|guardaroba|maglion|felp|pantalon|gonn)/i.test(inputText)) setAbitiBox(true);
      toast.success(`${data.items.length} oggetti estratti${nmCap ? ` · cliente: ${nmCap}` : ""}`);
    } catch { toast.error("Errore durante l'analisi AI"); } finally { setLoading(false); }
  };

  return (
    <StepShell icon={FileText} title={STEP_TITLES.text_input} subtitle="Incolla il messaggio del cliente, analizzalo con l'AI e controlla gli oggetti qui sotto.">
      <Textarea value={inputText} onChange={(e) => setInputText(e.target.value)} rows={5}
        placeholder="Es. 'Sono Mario Rossi, ho un divano 3 posti 200x90x85, 5 scatole, una lavatrice, zona 20142'"
        data-testid="wizard-text-input" autoFocus />
      <Button onClick={analyze} disabled={loading || !inputText.trim()} className="bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="wizard-text-analyze">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {textResults ? "Rianalizza con AI" : "Analizza con AI"}
      </Button>

      {textResults && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ListChecks className="h-4 w-4 text-[#1a5f5a]" /> Controlla gli oggetti ({textResults.length})</div>
          <p className="text-xs text-slate-400">Attiva/disattiva, correggi nome, quantità e dimensioni. Premi Avanti per aggiungerli.</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {textResults.map((r) => (
              <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`wizard-extracted-${r.id}`}>
                <div className="flex items-center gap-3">
                  <Switch checked={r.include} onCheckedChange={(v) => setField(r.id, { include: v })} data-testid={`wizard-extracted-toggle-${r.id}`} />
                  <Input value={r.label} onChange={(e) => setField(r.id, { label: e.target.value })} className="h-8 text-sm flex-1 min-w-0" />
                  <Badge className={`${CONF[r.confidence] || CONF.medium} text-[10px]`}>{(r.confidence || "media")}</Badge>
                  <div className="flex items-center gap-1.5">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setField(r.id, { qty: Math.max(1, r.qty - 1) })}><Minus className="h-3 w-3" /></Button>
                    <span className="w-5 text-center text-sm">{r.qty}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setField(r.id, { qty: r.qty + 1 })}><Plus className="h-3 w-3" /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 pl-11">
                  {[["l", "Lung."], ["w", "Larg."], ["h", "Alt."]].map(([k, lbl]) => (
                    <div key={k}><label className="text-[10px] uppercase text-slate-400">{lbl} (cm)</label><Input type="number" value={r[k]} placeholder="—" onChange={(e) => setField(r.id, { [k]: e.target.value })} className="h-8" /></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CatalogSuggestion extras={extras} setExtras={setExtras} />
    </StepShell>
  );
}

/* ---------------- Image step (upload + inline review) ---------------- */
function ImageStep({ photos, setPhotos, detected, setDetected, extras, setExtras }) {
  const { setAnalysisImageUrl } = useQuote();
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileRef = useRef();
  const CONF = { high: { label: "Alta", cls: "bg-green-100 text-green-700", dot: "#22c55e", rank: 2 }, medium: { label: "Media", cls: "bg-amber-100 text-amber-700", dot: "#f59e0b", rank: 1 }, low: { label: "Bassa", cls: "bg-red-100 text-red-700", dot: "#ef4444", rank: 0 } };
  const toggle = (id) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, selected: !d.selected } : d));
  const upd = (id, patch) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, ...patch } : d));
  const updDims = (id, k, v) => setDetected((ds) => ds.map((d) => d.id === id ? { ...d, dims: { ...d.dims, [k]: Math.max(0, Math.round(Number(v) || 0)) } } : d));

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (photos.length + files.length > 15) { toast.error("Massimo 15 foto"); return; }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 15MB)`); continue; }
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((ps) => [...ps, { id: uid(), dataUrl: data.dataUrl, url: data.url }]);
        setAnalysisImageUrl(data.url);
      }
    } catch { toast.error("Errore durante il caricamento"); } finally { setUploading(false); }
  };

  const analyze = async () => {
    if (!photos.length) return;
    setAnalyzing(true); setProgress({ done: 0, total: photos.length });
    let counter = 0; const all = [];
    try {
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        try {
          const { data } = await api.post("/ai/analyze-photo", { imageUrl: p.dataUrl });
          (data.detectedItems || []).forEach((d) => {
            counter += 1;
            const l = Math.round(Number(d.length_cm) > 0 ? d.length_cm : 40);
            const w = Math.round(Number(d.width_cm) > 0 ? d.width_cm : 40);
            const h = Math.round(Number(d.height_cm) > 0 ? d.height_cm : 40);
            all.push({ id: uid(), num: counter, photoId: p.id, label: d.label || "Oggetto", marker: d.marker || { x: 0.5, y: 0.5 }, confidence: d.confidence || "medium", dims: { l, w, h }, disassemblable: !!d.is_disassemblable, catalogMatch: d.catalogMatch || null, qty: 1, selected: true });
          });
        } catch { toast.error(`Errore analisi foto ${i + 1}`); }
        setProgress({ done: i + 1, total: photos.length });
      }
      setDetected(all);
      toast.success(`${all.length} oggetti rilevati su ${photos.length} foto`);
    } finally { setAnalyzing(false); }
  };

  return (
    <StepShell icon={Camera} title={STEP_TITLES.image_upload} subtitle="Carica una o più foto, analizzale con l'AI e controlla gli oggetti qui sotto.">
      <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors p-6 text-center cursor-pointer" data-testid="wizard-image-dropzone">
        {uploading ? <Loader2 className="h-7 w-7 animate-spin mx-auto text-[#1a5f5a]" /> : <Upload className="h-7 w-7 mx-auto text-slate-400" />}
        <p className="text-sm text-slate-500 mt-2">Trascina le foto qui o clicca per caricare (max 15)</p>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="wizard-image-file-input" />
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative rounded-md overflow-hidden border border-slate-200">
              <img src={p.dataUrl} alt="Foto" className="w-full h-auto block bg-slate-50" />
              <button onClick={(e) => { e.stopPropagation(); setPhotos((ps) => ps.filter((x) => x.id !== p.id)); setDetected(null); }} className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {photos.length > 0 && (
        <Button onClick={analyze} disabled={analyzing} className="bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="wizard-image-analyze">
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {analyzing ? `Analisi ${progress.done}/${progress.total}…` : (detected ? "Analizza di nuovo" : "Analizza foto")}
        </Button>
      )}

      {detected && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ListChecks className="h-4 w-4 text-[#1a5f5a]" /> Controlla gli oggetti ({detected.length})</div>
          <p className="text-xs text-slate-400">Attiva/disattiva, correggi nome, dimensioni e quantità. Premi Avanti per aggiungerli.</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {[...detected].sort((a, b) => (CONF[a.confidence]?.rank ?? 1) - (CONF[b.confidence]?.rank ?? 1)).map((d) => {
              const conf = CONF[d.confidence] || CONF.medium;
              return (
                <div key={d.id} className={`border rounded-md p-3 ${d.confidence === "low" ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} data-testid={`wizard-detected-${d.num}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: conf.dot }}>{d.num}</span>
                    <Switch checked={d.selected} onCheckedChange={() => toggle(d.id)} data-testid={`wizard-detected-toggle-${d.num}`} />
                    <Input value={d.label} onChange={(e) => upd(d.id, { label: e.target.value })} className="h-8 text-sm flex-1 min-w-0" />
                    <Badge className={`${conf.cls} text-[10px]`}>{conf.label}</Badge>
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(d.id, { qty: Math.max(1, d.qty - 1) })}><Minus className="h-3 w-3" /></Button>
                      <span className="w-5 text-center text-sm">{d.qty}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(d.id, { qty: d.qty + 1 })}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 pl-9">
                    <span className="text-[11px] text-slate-400">Dim (cm):</span>
                    {["l", "w", "h"].map((k) => <Input key={k} type="number" value={d.dims[k]} onChange={(e) => updDims(d.id, k, e.target.value)} className="h-7 w-16 text-sm" />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CatalogSuggestion extras={extras} setExtras={setExtras} />
    </StepShell>
  );
}

/* ---------------- Ritiro step ---------------- */
function RitiroStep() {
  const { ritiro, setRitiro, ritiroTotal, effectiveGaugePeriods } = useQuote();
  const [capLoading, setCapLoading] = useState(false);
  const upd = (patch) => setRitiro((r) => ({ ...r, ...patch }));
  const freePeriods = Array.isArray(ritiro.freePeriods) ? ritiro.freePeriods.map(Number) : [];
  const toggleFree = (m) => { const cur = freePeriods; upd({ freePeriods: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b) }); };

  const lookupCap = async () => {
    const cap = (ritiro.cap || "").trim();
    if (cap.length < 5) return;
    setCapLoading(true);
    try {
      let res;
      try { res = (await api.post("/ai/lookup-cap", { cap })).data; }
      catch { res = (await api.post("/ai/estimate-travel", { cap, citta: ritiro.citta })).data; }
      upd({ citta: res.citta || ritiro.citta, distanzaKm: res.distanzaKm ?? ritiro.distanzaKm, tempoMin: res.tempoMin ?? ritiro.tempoMin });
      toast.success(`Località trovata: ${res.citta || cap}`);
    } catch { toast.error("Impossibile calcolare la distanza per questo CAP."); } finally { setCapLoading(false); }
  };

  return (
    <StepShell icon={Truck} title={STEP_TITLES.ritiro} subtitle="Attiva il ritiro e indica il CAP: distanza e tempo si calcolano da soli. Questo passo è facoltativo.">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Ritiro a domicilio</span>
        <Switch checked={ritiro.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="wizard-ritiro-toggle" />
      </div>
      {ritiro.enabled && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">CAP</Label>
              <Input value={ritiro.cap} maxLength={5} onChange={(e) => upd({ cap: e.target.value.replace(/\D/g, "") })} onBlur={lookupCap} onKeyDown={(e) => e.key === "Enter" && lookupCap()} placeholder="20090" className="mt-1" data-testid="wizard-ritiro-cap" />
            </div>
            <div>
              <Label className="text-xs">Città / Zona</Label>
              <div className="flex gap-2 mt-1 items-center">
                <Input value={ritiro.citta} onChange={(e) => upd({ citta: e.target.value })} placeholder="Milano" data-testid="wizard-ritiro-citta" />
                {capLoading && <Loader2 className="h-4 w-4 animate-spin text-[#1a5f5a] shrink-0" />}
              </div>
            </div>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
            <label className="flex items-center gap-2 text-sm text-emerald-800 cursor-pointer">
              <Gift className="h-4 w-4" /> Ritiro gratuito
              <Switch checked={!!ritiro.freeEnabled} onCheckedChange={(v) => upd(v && freePeriods.length === 0 ? { freeEnabled: true, freePeriods: [12] } : { freeEnabled: v })} data-testid="wizard-ritiro-free-toggle" />
            </label>
            {ritiro.freeEnabled && (
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <span className="text-xs text-emerald-700">per impegni minimi:</span>
                {effectiveGaugePeriods.map((p) => {
                  const sel = freePeriods.includes(p.months);
                  return <button key={p.months} type="button" onClick={() => toggleFree(p.months)} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-100"}`}>{p.months} {p.months === 1 ? "mese" : "mesi"}</button>;
                })}
              </div>
            )}
          </div>
          <div className="bg-slate-50 rounded-md p-3 flex justify-between text-sm font-semibold text-slate-800">
            <span>Costo ritiro stimato</span><span className="text-[#1a5f5a]" data-testid="wizard-ritiro-total">€{formatEur(ritiroTotal)}</span>
          </div>
        </div>
      )}
    </StepShell>
  );
}

/* ---------------- Cliente step ---------------- */
function ClienteStep() {
  const { clienteNome, setClienteNome } = useQuote();
  return (
    <StepShell icon={User} title={STEP_TITLES.cliente} subtitle="Inserisci il nome del cliente (facoltativo): comparirà come intestazione della chat.">
      <div>
        <Label className="text-xs">Nome cliente</Label>
        <Input value={clienteNome} onChange={(e) => setClienteNome(e.target.value)} placeholder="Es. Mario Rossi" className="mt-1" data-testid="wizard-cliente-nome" autoFocus />
      </div>
    </StepShell>
  );
}

/* ---------------- Riepilogo step ---------------- */
function RiepilogoStep({ baseMode }) {
  const { items, monthlyTotal, ritiro, ritiroTotal, discounts, clienteNome, settings, areaData } = useQuote();
  const durDiscs = parseDurationDiscounts(settings?.durationDiscounts)
    .filter((d) => Number(d.months) > 0 && Number(d.percent) > 0)
    .sort((a, b) => Number(b.months) - Number(a.months));
  const isArea = baseMode === "area";
  const volNetto = (areaData.area || 0) * (areaData.altezza || 0) * ((areaData.riempimento || 0) / 100);

  const Row = ({ label, value, strong }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${strong ? "font-semibold text-slate-800" : "text-slate-600"}`}>{label}</span>
      <span className={`text-sm tabular-nums ${strong ? "font-semibold text-[#1a5f5a]" : "text-slate-700"}`}>{value}</span>
    </div>
  );

  return (
    <StepShell icon={ClipboardList} title={STEP_TITLES.riepilogo} subtitle="Controlla il preventivo prima di generare il messaggio. Usa Indietro per modificare.">
      {clienteNome?.trim() && (
        <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="wizard-riepilogo-cliente">
          <User className="h-4 w-4 text-[#1a5f5a]" /> Cliente: <span className="font-semibold">{clienteNome}</span>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg p-4">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{isArea ? "Deposito" : `Oggetti (${items.length})`}</div>
        {isArea ? (
          <Row label={`Volume netto (${(areaData.area || 0)} m² · ${areaData.riempimento}%)`} value={`${volNetto.toFixed(2)} m³`} />
        ) : items.length === 0 ? (
          <p className="text-slate-400 text-sm">Nessun oggetto.</p>
        ) : (
          <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto" data-testid="wizard-riepilogo-items">
            {items.map((it) => {
              const d = it.dims || {};
              const dim = (d.l > 0 && d.w > 0 && d.h > 0) ? ` · ${d.l}×${d.w}×${d.h} cm` : "";
              return <Row key={it.id} label={`${it.qty}× ${it.tipo}${dim}`} value={`€${formatEur(effectivePrice(it, discounts) * it.qty)}/mese`} />;
            })}
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg p-4">
        <Row label="Totale mensile" value={`€${formatEur(monthlyTotal)}/mese`} strong />
        <div className="border-t border-slate-100 my-1" />
        {durDiscs.map((d) => (
          <Row key={d.months} label={`${d.months} mesi (-${d.percent}%)`} value={`€${formatEur(monthlyTotal * (1 - d.percent / 100))}/mese`} />
        ))}
        {ritiro.enabled && ritiro.cap ? (
          <>
            <div className="border-t border-slate-100 my-1" />
            <Row label={`Ritiro a domicilio${ritiro.citta ? ` (${ritiro.citta})` : ""}`} value={ritiroTotal > 0 ? `€${formatEur(ritiroTotal)}` : "Gratis"} />
          </>
        ) : null}
      </div>
    </StepShell>
  );
}

/* ---------------- Result step ---------------- */
const renderFormatted = (line, i) => {
  const nodes = []; let buf = ""; let bold = false, italic = false, key = 0;
  const flush = () => { if (!buf) return; let node; if (bold && italic) node = <strong key={key}><em>{buf}</em></strong>; else if (bold) node = <strong key={key}>{buf}</strong>; else if (italic) node = <em key={key} className="text-[#54656f]">{buf}</em>; else node = <span key={key}>{buf}</span>; nodes.push(node); key += 1; buf = ""; };
  for (const ch of line) { if (ch === "*") { flush(); bold = !bold; } else if (ch === "_") { flush(); italic = !italic; } else buf += ch; }
  flush();
  return <div key={i} className="min-h-[6px]">{nodes}</div>;
};

function ResultStep({ baseMode }) {
  const {
    mode, items, areaData, monthlyTotal, ritiro, ritiroTotal, discounts, settings, catalog,
    imballaggioTotal, smontaggioTotal, abitiBox, freeMaterials, setAbitiBox, setFreeMaterials,
    setPolishedMessage, clienteNome, saveNow, saveStatus, savedQuoteNum,
  } = useQuote();
  const [copied, setCopied] = useState(false);
  const [edit, setEdit] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false); // user edited or AI-polished the text

  const abitiPrice = useMemo(() => {
    if (settings?.msgAbitiPrice != null) return Number(settings.msgAbitiPrice);
    const c = (catalog || []).find((x) => /porta abiti/i.test(x.label || ""));
    return c ? c.price_eur : 18;
  }, [settings, catalog]);

  const computed = useMemo(() => buildWhatsAppMessage({
    mode: baseMode === "area" ? "area_done" : mode, items, areaData, monthlyTotal, ritiro, ritiroTotal,
    allDiscounts: discounts, durationDiscounts: settings?.durationDiscounts, smontaggio: ritiro.smontaggio, smontaggioTotal,
    imballaggio: ritiro.imballaggio, imballaggioTotal, texts: settings, abitiBox, abitiPrice, freeMaterials, adaptedWhy: "",
  }), [baseMode, mode, items, areaData, monthlyTotal, ritiro, ritiroTotal, discounts, settings, smontaggioTotal, imballaggioTotal, abitiBox, abitiPrice, freeMaterials]);

  // keep the message in sync with the live quote until the user manually edits/optimizes it
  useEffect(() => {
    if (!dirty) setDraft(computed);
  }, [computed, dirty]);

  const display = draft;
  const nowTime = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const contactName = (clienteNome && clienteNome.trim()) ? clienteNome : "Cliente";

  const onEdit = (val) => { setDraft(val); setDirty(true); setPolishedMessage(val); };

  const copy = async () => {
    const ok = await copyText(display, waMarkupToHtml(display));
    if (ok) { setCopied(true); toast.success("Messaggio copiato con formattazione!"); setTimeout(() => setCopied(false), 2000); }
    else toast.error("Impossibile copiare");
  };

  const resetMsg = () => { setDirty(false); setDraft(computed); setPolishedMessage(""); toast.success("Messaggio ricalcolato dal preventivo"); };

  const enhance = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/polish-message", { message: display, instruction: aiInstruction || "", prompt: settings?.msgPolishPrompt || "" });
      if (data.polished) { setDraft(data.polished); setDirty(true); setPolishedMessage(data.polished); setAiInstruction(""); toast.success("Messaggio ottimizzato con l'AI"); }
    } catch { toast.error("Impossibile ottimizzare il messaggio"); } finally { setAiBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const ok = await saveNow();
      if (ok !== false) toast.success("Preventivo salvato");
      else toast.error("Aggiungi almeno un contenuto prima di salvare");
    } catch { toast.error("Errore durante il salvataggio"); } finally { setSaving(false); }
  };

  return (
    <StepShell icon={MessageCircle} title={STEP_TITLES.result} subtitle="Ecco il messaggio WhatsApp pronto. Puoi modificarlo, copiarlo o inviarlo.">
      {/* optional message sections */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="wizard-result-toggles">
        <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer">
          <span className="flex items-center gap-2 text-sm text-slate-700">👗 Scatola porta abiti</span>
          <Switch checked={!!abitiBox} onCheckedChange={setAbitiBox} data-testid="wizard-toggle-abiti" />
        </label>
        <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 cursor-pointer">
          <span className="flex items-center gap-2 text-sm text-slate-700">🎁 Materiali d'imballaggio gratuiti</span>
          <Switch checked={!!freeMaterials} onCheckedChange={setFreeMaterials} data-testid="wizard-toggle-imballaggio" />
        </label>
      </div>

      <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-white">
        <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-slate-100">
          <div className="h-9 w-9 rounded-full bg-[#dfd3f3] flex items-center justify-center shrink-0"><User className="h-4 w-4 text-[#8b7ec8]" fill="#8b7ec8" /></div>
          <div className="min-w-0 flex-1"><div className="text-[15px] font-semibold text-[#111b21] leading-tight truncate">{contactName}</div><div className="text-xs text-[#667781] leading-tight truncate">ultimo accesso oggi alle {nowTime}</div></div>
        </div>
        {edit ? (
          <div className="p-3">
            <Textarea value={display} onChange={(e) => onEdit(e.target.value)} rows={14} className="text-sm resize-none font-mono" data-testid="wizard-message-input" />
            <p className="text-[11px] text-slate-400 mt-1.5">Usa *grassetto* e _corsivo_ come su WhatsApp.</p>
          </div>
        ) : (
          <div className="wa-chat-bg p-3 min-h-[240px] max-h-[420px] overflow-y-auto flex flex-col">
            <div className="wa-bubble ml-auto max-w-[88%] px-2.5 py-1.5 mt-1 mr-2 text-[13.5px] leading-snug" data-testid="wizard-message">
              {display.split("\n").map(renderFormatted)}
              <div className="text-[10px] text-[#667781] text-right mt-1">{nowTime}</div>
            </div>
          </div>
        )}
      </div>

      <Button onClick={copy} className="w-full bg-[#1a5f5a] hover:bg-[#134744] gap-1.5" data-testid="wizard-copy-button">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copiato" : "Copia messaggio"}
      </Button>

      <Button onClick={save} disabled={saving || saveStatus === "saving"} variant="outline" className="w-full gap-1.5 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5" data-testid="wizard-save-button">
        {(saving || saveStatus === "saving") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {savedQuoteNum ? `Preventivo salvato #${savedQuoteNum} — salva di nuovo` : "Salva preventivo"}
      </Button>

      {/* AI optimization with custom instruction */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Wand2 className="h-4 w-4 text-[#1a5f5a]" /> Ottimizza con l'AI</div>
        <Textarea value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} rows={2}
          placeholder="Istruzione facoltativa per l'AI… (es. 'tono più formale', 'evidenzia lo sconto 12 mesi', 'più breve')"
          className="text-sm bg-white resize-none" data-testid="wizard-ai-instruction" />
        <Button onClick={enhance} disabled={aiBusy} className="w-full bg-[#1a5f5a] hover:bg-[#134744] gap-1.5" data-testid="wizard-enhance">
          {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {aiBusy ? "Ottimizzo…" : (aiInstruction.trim() ? "Applica istruzione con AI" : "Ottimizza con AI")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setEdit((e) => !e)} className="gap-1.5" data-testid="wizard-edit-toggle"><FileText className="h-4 w-4" /> {edit ? "Anteprima" : "Modifica testo"}</Button>
        {dirty && <Button variant="ghost" onClick={resetMsg} className="gap-1.5 text-slate-500" data-testid="wizard-reset-msg"><RotateCcw className="h-4 w-4" /> Ricalcola</Button>}
      </div>
    </StepShell>
  );
}

/* ---------------- Main wizard ---------------- */
export default function WizardMode({ baseMode, onClose }) {
  const ctx = useQuote();
  const { confirmArea, resolveEntries, replaceItems, addItemsBulk, setImagePhotos, setMode, mode } = ctx;
  const steps = STEPS[baseMode] || STEPS.objects;
  const [step, setStep] = useState(0);
  const stepKey = steps[step];

  // local analysis state for text/image
  const [textResults, setTextResults] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [detected, setDetected] = useState(null);
  const [catalogExtras, setCatalogExtras] = useState([]);
  const [advancing, setAdvancing] = useState(false);

  const Meta = MODE_META[baseMode] || MODE_META.objects;

  const canNext = useMemo(() => {
    if (stepKey === "items") return ctx.items.length > 0;
    if (stepKey === "area") return (ctx.areaData.area || 0) > 0;
    if (stepKey === "text_input") return (textResults || []).some((r) => r.include) || catalogExtras.length > 0;
    if (stepKey === "image_upload") return (detected || []).some((d) => d.selected) || catalogExtras.length > 0;
    return true;
  }, [stepKey, ctx.items, ctx.areaData, textResults, detected, catalogExtras]);

  const extrasEntries = () => catalogExtras.map((e) => ({ label: e.label, qty: e.qty, catalogMatch: e.label, dims: { l: 0, w: 0, h: 0 } }));

  const commitTextReview = async () => {
    const chosen = (textResults || []).filter((r) => r.include);
    const entries = [
      ...chosen.map((r) => ({ label: r.label, qty: r.qty, catalogMatch: r.catalogMatch, dims: { l: parseFloat(r.l) || 0, w: parseFloat(r.w) || 0, h: parseFloat(r.h) || 0 } })),
      ...extrasEntries(),
    ];
    const built = await resolveEntries(entries);
    // keep the labels the user reviewed for the extracted items (avoid catalog rename)
    chosen.forEach((r, i) => { if (built[i]) built[i].tipo = r.label; });
    replaceItems(built);
    setMode("text_done");
  };

  const commitImageReview = async () => {
    const pricePerM3 = ctx.settings?.pricePerM3 ?? 62.5;
    const chosen = (detected || []).filter((d) => d.selected);
    const built = chosen.map((d) => {
      const base = { id: uid(), tipo: d.label, qty: d.qty, dims: { ...d.dims }, note: "", fachMinutes: 0, smontMinutes: 0, imbMinutes: 0, includeSmont: d.disassemblable, includeImb: false, photoId: d.photoId, marker: d.marker, confidence: d.confidence };
      return { ...base, pricePerMonth: ceil10(calcVolume(d.dims.l, d.dims.w, d.dims.h) * pricePerM3), fromCatalog: false, isPriceOverridden: true, isDimOverridden: true };
    });
    setImagePhotos(photos.map((p) => ({ id: p.id, url: p.url, dataUrl: p.dataUrl })));
    let all = built;
    if (catalogExtras.length) { const extra = await resolveEntries(extrasEntries()); all = [...built, ...extra]; }
    addItemsBulk(all);
    setMode("image_done");
  };

  const next = async () => {
    if (!canNext || advancing) return;
    setAdvancing(true);
    try {
      if (stepKey === "text_input") await commitTextReview();
      if (stepKey === "image_upload") await commitImageReview();
      // materialize the area item BEFORE the riepilogo/result steps so totals are correct
      if (baseMode === "area" && steps[step + 1] === "riepilogo") confirmArea();
      setStep((s) => Math.min(steps.length - 1, s + 1));
    } finally { setAdvancing(false); }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const seeDetails = () => {
    if (baseMode === "area" && mode !== "area_done") confirmArea();
    onClose();
  };

  const renderStep = () => {
    switch (stepKey) {
      case "items": return <ItemsStep />;
      case "area": return <AreaStep />;
      case "text_input": return <TextStep textResults={textResults} setTextResults={setTextResults} extras={catalogExtras} setExtras={setCatalogExtras} />;
      case "image_upload": return <ImageStep photos={photos} setPhotos={setPhotos} detected={detected} setDetected={setDetected} extras={catalogExtras} setExtras={setCatalogExtras} />;
      case "ritiro": return <RitiroStep />;
      case "cliente": return <ClienteStep />;
      case "riepilogo": return <RiepilogoStep baseMode={baseMode} />;
      case "result": return <ResultStep baseMode={baseMode} />;
      default: return null;
    }
  };

  const isResult = stepKey === "result";

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4" data-testid="wizard-overlay">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <Meta.icon className="h-5 w-5 text-[#1a5f5a] shrink-0" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400">Modalità guidata · {Meta.title}</div>
              <div className="text-sm font-semibold text-slate-700 truncate">Passo {step + 1} di {steps.length} — {STEP_TITLES[stepKey]}</div>
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} className="text-slate-400 shrink-0" data-testid="wizard-close"><X className="h-5 w-5" /></Button>
        </div>

        {/* progress bar */}
        <div className="h-1 bg-slate-100 shrink-0"><div className="h-full bg-[#1a5f5a] transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>

        {/* body */}
        <div className="p-5 sm:p-6 overflow-y-auto grow" data-testid="wizard-body">
          {renderStep()}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 shrink-0 bg-white">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="gap-1.5 text-slate-500" data-testid="wizard-back"><ArrowLeft className="h-4 w-4" /> Indietro</Button>
          {isResult ? (
            <Button onClick={seeDetails} variant="outline" className="gap-1.5 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5" data-testid="wizard-see-details">Vedi i dettagli <ArrowRight className="h-4 w-4" /></Button>
          ) : (
            <Button onClick={next} disabled={!canNext || advancing} className="gap-1.5 bg-[#1a5f5a] hover:bg-[#134744]" data-testid="wizard-next">
              {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {stepKey === "riepilogo" ? "Genera messaggio" : "Avanti"} <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
